<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useSessionStore } from '@Stores';
import type { GenericField } from '@Types';
import type { FieldSchema } from '../../../types';

export interface HoldStatus {
  days: number;
  id: string;
  name: string;
  position: number;
  value: string;
}

const props = defineProps<{
  error?: string;
  field?: FieldSchema;
  label?: string;
  options?: HoldStatus[];
}>();

const { shelterProfile } = storeToRefs(useSessionStore());

const { t } = useI18n();

const model = defineModel<any>();

const customModel = ref();
const holdModel = ref();
const minDate = ref(new Date());
const radioModel = ref<'holdStatus' | 'customDate'>('holdStatus');

const commentModel = ref();
const customReasonModel = ref();
const endDateModel = ref<Date | null | undefined>(undefined);
const holdReasonModel = ref();
const holdTypeModel = ref<'holdStatus' | 'custom'>('holdStatus');
const startDateModel = ref<Date | null | undefined>(undefined);

const holdTypeOptions = computed((): GenericField[] => [
  {
    name: t('animals.individualPage.holdLimits.selectHoldStatus'),
    value: 'holdStatus',
  },
  {
    name: t('animals.individualPage.holdLimits.customStatus'),
    value: 'custom',
  },
]);
const lengthOfHold = computed(() => {
  if (!startDateModel?.value || !endDateModel?.value) return t('animals.individualPage.holdLimits.emptyLengthOfHold');

  const start = new Date(startDateModel.value);
  const end = new Date(endDateModel.value);
  const diffTime = Math.abs(end.getTime() - start.getTime());

  const totalMinutes = Math.floor(diffTime / (1000 * 60));
  const minutes = totalMinutes % 60;

  const totalHours = Math.floor(diffTime / (1000 * 60 * 60));
  const hours = totalHours % 24;

  const days = Math.floor(totalHours / 24);
  const daysString = days > 1 ? 'Days' : 'Day';
  const hoursString = hours > 1 ? 'Hours' : 'Hour';
  const minutesString = minutes > 1 ? 'Minutes' : 'Minute';

  return `${days} ${daysString} ${hours} ${hoursString} ${minutes} ${minutesString}`;
});

const options = computed((): any[] | undefined => {
  const option = props?.field?.edit?.options || props?.options || [];

  if (!Array.isArray(option) || option.length === 0) return undefined;

  return option.slice().sort((a: any, b: any) => a.position - b.position);
});

const startDateModelComputed = computed({
  get: (): Date | undefined => startDateModel.value ?? undefined,
  set: (value: Date | undefined) => {
    startDateModel.value = value ?? undefined;
  },
});

const endDateModelComputed = computed({
  get: (): Date | undefined => endDateModel.value ?? undefined,
  set: (value: Date | undefined) => {
    endDateModel.value = value ?? undefined;
  },
});

const radioOptions = computed((): GenericField[] => [
  {
    name: t('components.holdStatus.holdStatus'),
    value: 'holdStatus',
  },
  {
    name: t('components.holdStatus.customDate'),
    value: 'customDate',
  },
]);

watch(customModel, () => setCustomDate(customModel.value));
watch(radioModel, () => resetModels());
watch(holdTypeModel, () => switchHoldType());
watch(holdReasonModel, () => setDatesFromHoldReason());
watch([lengthOfHold, commentModel, customReasonModel], () => setModel());
watch([holdReasonModel, customReasonModel], ([newHoldReason, newCustomReason]) => {
  if (!newHoldReason && !newCustomReason) clearAllModels();
});

onMounted(() => initModels());

function resetModels() {
  holdModel.value = undefined;
  model.value = undefined;
}

function clearAllModels() {
  holdReasonModel.value = undefined;
  commentModel.value = undefined;
  customModel.value = undefined;
  customReasonModel.value = undefined;
  endDateModel.value = undefined;
  holdModel.value = undefined;
  model.value = undefined;
  startDateModel.value = undefined;
}

function initModels() {
  if (!model.value) return;
  if (model.value.radioModel !== 'customDate') return (holdModel.value = model.value);

  radioModel.value = model.value.radioModel;
  customModel.value = model.value.expirationDate;
}

function setCustomDate(date: any) {
  if (radioModel.value === 'holdStatus') return (model.value = date);

  if (!date || isNaN(new Date(date).getTime())) {
    model.value = undefined;
    return;
  }

  const dateObj = new Date(date);
  const expirationDate = dateObj.toISOString();
  const customDate = formatCustomHoldExpirationDate(dateObj);

  model.value = { customDate, expirationDate };
}

// HELPERS
function formatCustomHoldExpirationDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `Custom Hold Expiration Date - ${month}-${day}-${year}`;
}

// ---------- New Hold Limits ----------

function switchHoldType() {
  holdReasonModel.value = undefined;
  customReasonModel.value = undefined;

  model.value = undefined;
}

function setDatesFromHoldReason() {
  if (!holdReasonModel.value) return;

  const now = new Date();
  startDateModel.value = now;
  endDateModel.value = now;

  const days = Number(holdReasonModel.value.days);
  const endDateCalculated = new Date(startDateModel.value);
  endDateCalculated.setDate(startDateModel.value.getDate() + days);
  endDateModel.value = endDateCalculated;

  setModel();
}

function setModel() {
  if (!shelterProfile?.value?.enable_hold_limits) return;

  const notValidValues = !holdReasonModel.value && !customReasonModel.value;
  if (notValidValues) return;

  if (!startDateModel.value || !endDateModel.value) return;

  const isCustomHoldStatus = holdTypeModel.value === 'custom';
  model.value = {
    hold_settings_id: isCustomHoldStatus ? null : holdReasonModel.value?._id?.$oid,
    label: isCustomHoldStatus ? customReasonModel.value : holdReasonModel.value.label,
    name: isCustomHoldStatus ? null : holdReasonModel.value.name,
    color: isCustomHoldStatus ? null : holdReasonModel.value.color,
    start_date: startDateModel.value,
    end_date: endDateModel.value,
    custom_hold: isCustomHoldStatus,
    comment: commentModel.value,
  };
}
</script>

<template>
  <div>
    <div
      v-if="!shelterProfile?.enable_hold_limits"
      class="flex flex-col gap-2"
    >
      <AFieldRadio
        v-model="radioModel"
        :options="radioOptions"
        :error
      />

      <AFieldDropdown
        v-if="radioModel === 'holdStatus'"
        v-model="holdModel"
        :error
        :options
        optionLabel="label"
        @change="setCustomDate(holdModel)"
      />

      <MFieldDatePicker
        v-else
        v-model="customModel"
        :error
        :minDate="minDate"
        :noModelOnStart="!field?.settings?.required"
      />
    </div>

    <div
      v-else
      class="relative flex flex-col gap-6 pb-3 pt-1"
    >
      <AFieldSeparator
        :class="[error ? 'bg-red-600' : 'bg-gray-300', 'absolute top-[-22px] z-[-1] h-[1px] border-0']"
      />

      <AFieldRadio
        v-model="holdTypeModel"
        :options="holdTypeOptions"
        :error
      />

      <div class="flex flex-col gap-1">
        <AFieldLabel
          :label="$t('animals.individualPage.holdLimits.holdReason')"
          :error
          :required="field?.settings.required"
        />

        <AFieldDropdown
          v-if="holdTypeModel === 'holdStatus'"
          v-model="holdReasonModel"
          :error
          :options
          optionLabel="label"
        />

        <AFieldInputText
          v-else
          v-model="customReasonModel"
          :error
        />
      </div>

      <div class="flex flex-col gap-1">
        <AFieldLabel
          :label="$t('common.startDate')"
          :error
          :required="field?.settings.required"
        />

        <MFieldDatePicker
          v-model="startDateModelComputed"
          removeClearIcon
          showTime
          :error
          :disabled="!holdReasonModel && !customReasonModel"
          :noModelOnStart="!field?.settings?.required && !holdReasonModel"
        />
      </div>

      <div class="flex flex-col gap-1">
        <AFieldLabel
          :label="$t('common.endDate')"
          :error
          :required="field?.settings.required"
        />

        <MFieldDatePicker
          v-model="endDateModelComputed"
          removeClearIcon
          showTime
          :error
          :disabled="!holdReasonModel && !customReasonModel"
          :minDate="startDateModelComputed"
          :noModelOnStart="!field?.settings?.required && !holdReasonModel"
        />
      </div>

      <div class="flex gap-1">
        <AFieldLabel :label="$t('animals.individualPage.holdLimits.lengthOfHold') + ':'" />

        <p class="text-sm">{{ lengthOfHold }}</p>
      </div>

      <div class="flex flex-col gap-1">
        <AFieldLabel :label="$t('animals.individualPage.holdLimits.comments')" />

        <AFieldTextArea v-model="commentModel" />
      </div>
    </div>

    <AFieldSeparator :class="[error ? 'bg-red-600' : 'bg-gray-300', 'h-[1px] border-0']" />
  </div>
</template>
